import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Calendar, ChevronDown, ChevronRight } from 'lucide-react'
import Card from '../shared/Card'
import Button from '../shared/Button'
import DataTable from '../shared/DataTable'
import PaginationFooter from '../shared/PaginationFooter'
import { usePagination } from '../../hooks/usePagination'
import { formatCurrency, formatDate } from '../../utils/helpers'
import { PAGINATION, STATUS_COLORS } from '../../utils/dashboardConstants'
import DocumentItem from '../shared/DocumentItem'

/**
 * Investment Timeline component for displaying investment portfolio
 * Handles both mobile timeline view and desktop data table
 * Supports accordion mode when used on portfolio detail pages
 * 
 * @param {Array} timelineData - Array of investment objects
 * @param {Array} timelineGroups - Array of grouped investments by quarter
 * @param {boolean} loading - Loading state
 * @param {boolean} isOnDetailPage - Whether component is used on portfolio detail page (enables accordion mode)
 */
const InvestmentTimeline = ({ timelineData, timelineGroups, loading, isOnDetailPage = false }) => {
  const navigate = useNavigate()
  const { currentPage, totalPages, currentPageData, setCurrentPage } = usePagination(
    timelineGroups, 
    PAGINATION.MOBILE_TIMELINE_PAGE_SIZE
  )
  
  // State for accordion functionality when on detail page
  const [expandedInvestments, setExpandedInvestments] = useState(new Set())

  /**
   * Toggle accordion state for a specific investment
   * @param {string} investmentId - ID of the investment to toggle
   */
  const toggleInvestmentExpansion = (investmentId) => {
    setExpandedInvestments(prev => {
      const newSet = new Set(prev)
      if (newSet.has(investmentId)) {
        newSet.delete(investmentId)
      } else {
        newSet.add(investmentId)
      }
      return newSet
    })
  }

  /**
   * Handle investment click - either navigate or toggle accordion
   * @param {Object} investment - Investment object
   */
  const handleInvestmentClick = (investment) => {
    if (isOnDetailPage) {
      // On detail page, toggle accordion instead of navigating
      toggleInvestmentExpansion(investment.id)
    } else {
      // On dashboard, navigate to company detail page
      navigate(`/portfolio/${investment.startupId}`)
    }
  }

  return (
    <section>
      <Card
        title="Investment Timeline"
        subtitle="Your investment portfolio"
        className='mt-4'
      >
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-gray-500">Loading investment timeline...</div>
          </div>
        ) : timelineData.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-gray-500">No investments found</div>
          </div>
        ) : (
          <>
            {/* Mobile & Tablet: Timeline View */}
            <div className="divide-y divide-gray-200 lg:hidden">
              {currentPageData.map((group, groupIndex) => (
                <div key={group.quarter} className="p-4">
                  {/* Quarter header */}
                  <div className="flex items-center mb-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Calendar className="w-4 h-4 text-blue-600" />
                    </div>
                    <div className="ml-3">
                      <h3 className="font-semibold text-gray-900 text-sm">
                        {group.displayQuarter}
                      </h3>
                      <p className="text-xs text-gray-600">
                        {group.investments.length} {group.investments.length === 1 ? 'investment' : 'investments'}
                      </p>
                    </div>
                  </div>

                  {/* Investments for this date */}
                  <div className="ml-11">
                    {group.investments.map((investment, investmentIndex) => {
                      const isExpanded = expandedInvestments.has(investment.id)
                      
                      return (
                        <div key={investment.id}>
                          {/* Add separator line between investments if there are multiple */}
                          {investmentIndex > 0 && (
                            <div className="border-t border-gray-200"></div>
                          )}
                          <div className="p-4">
                            <div className="flex flex-col md:flex-row md:items-start md:justify-between">
                              <div className="flex-1 md:pr-3">
                                <div className="flex items-center justify-between mb-1">
                                  {investment.company && (
                                    <h3 className="font-semibold text-gray-900 text-base">
                                      {investment.company}
                                    </h3>
                                  )}
                                  {/* Only show status badge if it's not blank */}
                                  {investment.status && investment.status.trim() !== '' && (
                                    <span className={`px-2 py-1 text-xs rounded-full ${
                                      investment.status === 'Active' 
                                        ? STATUS_COLORS.ACTIVE
                                        : investment.status === 'Closed'
                                        ? STATUS_COLORS.CLOSED
                                        : STATUS_COLORS.DEFAULT
                                    }`}>
                                      {investment.status}
                                    </span>
                                  )}
                                </div>
                                <div className="space-y-1">
                                  <p className="text-sm text-gray-600">
                                    Amount: <span className="font-medium text-gray-900">
                                      {formatCurrency(parseFloat(investment.totalAmountInvested || 0), 'CAD')}
                                    </span>
                                  </p>
                                  <p className="text-sm text-gray-600">
                                    SPV Investment Date: <span className="font-medium text-gray-900">
                                      {formatDate(investment.investmentDate, 'MMM dd, yyyy')}
                                    </span>
                                  </p>
                                </div>
                              </div>
                              <div className="md:text-right md:ml-4 mt-3 md:mt-0">
                                <Button
                                  variant="outline"
                                  size="small"
                                  className="text-sm flex items-center gap-1 transition-all duration-200 hover:scale-105"
                                  onClick={() => handleInvestmentClick(investment)}
                                >
                                  {isOnDetailPage ? (
                                    <>
                                      {isExpanded ? 'Hide Details' : 'View Details'}
                                      <div className={`transition-transform duration-300 ease-in-out ${
                                        isExpanded ? 'rotate-0' : '-rotate-90'
                                      }`}>
                                        <ChevronDown className="w-4 h-4" />
                                      </div>
                                    </>
                                  ) : (
                                    'View Details'
                                  )}
                                </Button>
                              </div>
                            </div>
                            
                            {/* Accordion content - only show when expanded on detail page */}
                            {/* Moved outside flex container to span full width */}
                            {isOnDetailPage && (
                              <div 
                                className={`overflow-hidden transition-all duration-300 ease-in-out ${
                                  isExpanded 
                                    ? 'max-h-96 opacity-100 mt-4' 
                                    : 'max-h-0 opacity-0 mt-0'
                                }`}
                              >
                                <div className="p-4 bg-gray-50 rounded-lg border transition-all duration-300 ease-in-out">
                                  <div className="space-y-3 animate-in fade-in-0 slide-in-from-top-2 duration-500">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      <div className="transition-all duration-300 hover:bg-gray-100 p-2 rounded-md">
                                        <p className="text-sm font-medium text-gray-700 mb-1">SPV</p>
                                        <p className="text-sm text-gray-900">{investment.spv || 'Unknown'}</p>
                                      </div>
                                      <div className="transition-all duration-300 hover:bg-gray-100 p-2 rounded-md">
                                        <p className="text-sm font-medium text-gray-700 mb-1">Series</p>
                                        <p className="text-sm text-gray-900">{investment.series || 'Unknown'}</p>
                                      </div>
                                      <div className="transition-all duration-300 hover:bg-gray-100 p-2 rounded-md">
                                        <p className="text-sm font-medium text-gray-700 mb-1">Instrument</p>
                                        <p className="text-sm text-gray-900">{investment.instrument || 'Unknown'}</p>
                                      </div>
                                      <div className="transition-all duration-300 hover:bg-gray-100 p-2 rounded-md">
                                        <p className="text-sm font-medium text-gray-700 mb-1">Investment Summit</p>
                                        <p className="text-sm text-gray-900">{investment.investmentSummit || 'Unknown'}</p>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                {investment.documents && investment.documents.length > 0 && (
                                  <div className="mb-3 mt-3">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      {investment.documents.map((document) => (
                                        <DocumentItem 
                                          key={document.id}
                                          document={document}
                                        />
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              ))}

              {/* Pagination footer */}
              {timelineGroups.length > PAGINATION.MOBILE_TIMELINE_PAGE_SIZE && (
                <PaginationFooter
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                />
              )}
            </div>

            {/* Desktop: DataTable */}
            <div className="hidden lg:block">
              <DataTable
                columns={[
                  ...(timelineData.some(inv => inv.company) ? [{ key: 'company', label: 'Company' }] : []),
                  { 
                    key: 'investmentSummitQuarter', 
                    label: 'Investment Quarter'
                  },
                  { 
                    key: 'totalAmountInvested', 
                    label: 'Investment Amount',
                    render: (value) => formatCurrency(parseFloat(value || 0), 'CAD')
                  },
                  { key: 'spv', label: 'SPV' },
                  { key: 'series', label: 'Series' },
                  { 
                    key: 'investmentDate', 
                    label: 'SPV Investment Date',
                    render: (value) => formatDate(value, 'MMM dd, yyyy')
                  },
                  { key: 'instrument', label: 'Instrument' },
                  { key: 'documents', label: 'Documents' },
                ]}
                data={timelineData}
                pageSize={PAGINATION.DATA_TABLE_PAGE_SIZE}
                enableExport={true}
                exportFilename={`investments-export-${formatDate(new Date(), 'MMM-dd-yyyy')}`}
                onRowClick={(row) => {
                  // Handle row click based on context
                  handleInvestmentClick(row)
                }}
              />
            </div>
          </>
        )}
      </Card>
    </section>
  )
}

export default InvestmentTimeline
